import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageContainer } from "@/components/PageContainer";
import {
  Eyebrow,
  DisplayHeading,
  SectionRule,
  CTAButton,
} from "@/components/editorial";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Ethos, terms & waiver — Tiger's Eye" },
      { name: "description", content: "How Tiger's Eye reviews student drafts — what it does, what it refuses, and the student's responsibilities. Read the full ethos and waiver." },
      { property: "og:title", content: "Ethos, terms & waiver — Tiger's Eye" },
      { property: "og:description", content: "Critique only, never ghostwriting. The full ethos, terms and waiver for Tiger's Eye Academic Review." },
      { property: "og:url", content: "https://tigerseye.app/terms" },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/terms" }],
  }),
  component: Terms,
});

const DOES = [
  "Reviews structure, argument, clarity, evidence, referencing, and alignment with the assignment brief.",
  "Identifies unsupported claims, missing counterarguments, weak transitions, vague language, possible citation problems, and academic integrity risks.",
  "Asks Socratic questions that prompt the student to think.",
  "Explains what a marker may reasonably expect at the relevant level of study.",
  "Suggests further areas for the student to research independently.",
  "Provides a bibliography audit, flagging incomplete, inconsistent, implausible, or possibly fabricated references.",
  "Explains referencing rules - but does not manufacture citations for material not actually read by the student.",
];

const DOES_NOT = [
  "Does not write or rewrite assessed passages for submission.",
  "Does not produce a \u201Cmodel answer\u201D.",
  "Does not promise better marks, passing, grade uplift, or university acceptance.",
  "Does not insert arguments, evidence, or authorities the student has not independently understood.",
  "Does not conceal AI involvement or help evade detection tools.",
  "Does not paraphrase to bypass plagiarism controls.",
  "Does not generate fake references, page numbers, quotations, data, or fieldnotes.",
  "Does not complete analysis where the assessment requires the student's own reasoning.",
];

function Terms() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <PageContainer>
          <Eyebrow className="mb-3">Ethos, terms &amp; waiver</Eyebrow>
          <DisplayHeading level={1} balance className="text-primary mb-10">
            How Tiger&apos;s Eye works — and what it is not.
          </DisplayHeading>

          <blockquote
            className="border-l-2 border-[color:var(--color-editorial-accent)] pl-6 py-1 text-2xl text-primary leading-snug mb-10"
            style={{ fontFamily: "var(--display-font)" }}
          >
            Tiger&apos;s Eye helps students see their own draft more clearly. It does not
            help them outsource the work.
          </blockquote>

          <p className="text-ink-secondary leading-relaxed mb-12">
            Tiger&apos;s Eye is an academic draft-review and integrity-support tool. It
            reads what the student has already written, in light of the stated course,
            level, subject, assessment brief, marking criteria and referencing convention
            — and returns impartial, constructive critique. It is not a grade-improvement,
            essay-writing, coaching-to-pass or mark-maximising service.
          </p>

          <DisplayHeading level={3} className="text-primary mb-5">What Tiger&apos;s Eye does</DisplayHeading>
          <ul className="space-y-3 mb-12 text-ink-secondary">
            {DOES.map((d) => (
              <li key={d}>• {d}</li>
            ))}
          </ul>

          <DisplayHeading level={3} className="text-primary mb-5">What Tiger&apos;s Eye does not do</DisplayHeading>
          <ul className="space-y-3 mb-12 text-ink-secondary">
            {DOES_NOT.map((d) => (
              <li key={d}>• {d}</li>
            ))}
          </ul>

          <DisplayHeading level={3} className="text-primary mb-5">Responsibility &amp; waiver</DisplayHeading>
          <p className="text-ink-secondary leading-relaxed mb-4">
            The student remains solely responsible for the originality, accuracy,
            referencing and submission of all work. Tiger&apos;s Eye is a critique and
            integrity-support tool, not an authorship service. Use of Tiger&apos;s Eye does
            not certify a draft as plagiarism-free or AI-free; integrity risks are flagged
            neutrally for the student to consider before submission.
          </p>
          <p className="text-ink-secondary leading-relaxed mb-12">
            Where a course, institution or examination requires disclosure of AI assistance,
            it is the student&apos;s responsibility to disclose that Tiger&apos;s Eye was
            used and in what capacity. Tiger&apos;s Eye will not help to evade, minimise or
            misrepresent that disclosure.
          </p>

          <SectionRule spacing="mt-2 mb-10" />
          <CTAButton to="/" variant="primary">Back to home</CTAButton>
        </PageContainer>
      </main>
      <Footer />
    </div>
  );
}

